/**
 * AI output schema — JSON returned by OpenAI per PRD §FR5.
 * The agent's structured output is validated against this schema before any
 * decision (status transition, opt-out, send) is taken.
 */
import { Type } from '@sinclair/typebox';
/**
 * Intent labels detected by the AI. Mirrors the DB enum `intent_type`
 * (database-schema.md §3) so persisted values match.
 */
export const Intent = Type.Union([
    Type.Literal('confirma'),
    Type.Literal('cancela'),
    Type.Literal('reagenda'),
    Type.Literal('qualificacao_resposta'),
    Type.Literal('qualificado'),
    Type.Literal('desqualificado'),
    Type.Literal('pede_humano'),
    Type.Literal('opt_out'),
    Type.Literal('off_topic'),
    Type.Literal('saudacao'),
    Type.Literal('duvida'),
    Type.Literal('desconhecido'),
]);
export const Sentiment = Type.Union([
    Type.Literal('positive'),
    Type.Literal('neutral'),
    Type.Literal('negative'),
]);
/**
 * Suggested callback (PRD §FR5). `enabled=true` plus `scheduledAt` schedules
 * a callback worker.
 *
 * Story 9.4/9.5 (Epic E9) — first real producer/consumer: when the customer
 * asks to be recontacted later ("me chama mais tarde", "amanhã de manhã"),
 * the AI sets `enabled=true` and `scheduledAt` to the interpreted ISO instant
 * (`YYYY-MM-DDTHH:MM:SS-03:00` — America/Sao_Paulo, prompt-injected in
 * `conversation-worker.worker.ts`'s `applyCallbackInstructions`). The AI's
 * own `response` text ALWAYS confirms the interpreted time back to the
 * customer before the request is considered captured (Story 9.4 §AC3) — the
 * `scheduledAt` value alone is not sufficient confirmation. Story 9.5
 * consumes this field to create the `scheduled_dispatches` row + enqueue the
 * actual callback job.
 */
export const SuggestCallback = Type.Object({
    enabled: Type.Boolean(),
    // `format: 'date-time'` REMOVIDO: o OpenAI (response_format json_schema,
    // best-effort) não garante o formato ISO, e o output era rejeitado na
    // validação TypeBox de forma não-determinística. Validamos só o tipo string;
    // o parsing da data acontece no consumidor com fallback.
    scheduledAt: Type.Optional(Type.String()),
});
/**
 * Full output expected from the LLM. `response` capped at 500 chars to keep
 * WhatsApp deliverability predictable.
 */
export const AIOutput = Type.Object({
    intent: Intent,
    sentiment: Sentiment,
    // `maxLength: 500` REMOVIDO da VALIDAÇÃO: o OpenAI não garante o limite
    // (strict:true é incompatível com o schema do TypeBox — gera `const` nos
    // enums, que o OpenAI rejeita), e respostas > 500 chars derrubavam a validação
    // de forma não-determinística (IA não respondia). O tamanho é orientado pelo
    // prompt; truncamos no envio se necessário.
    response: Type.String(),
    suggestSendTemplate: Type.Optional(Type.String()),
    suggestCallback: Type.Optional(SuggestCallback),
    // Feature B (book-on-selection). Preenchido SOMENTE quando o cliente CONFIRMA
    // explicitamente um horário que a IA ofereceu na conversa (bloco "HORÁRIOS
    // DISPONÍVEIS" injetado no prompt). Deve conter o ISO EXATO de um dos slots
    // oferecidos (entre colchetes naquele bloco); fora desse caso, deixe nulo.
    // Sem `format: 'date-time'` pelo mesmo motivo de `suggestCallback.scheduledAt`
    // (o OpenAI não garante o formato e rejeitar o output quebraria a resposta);
    // o consumidor faz parsing defensivo e valida o ISO contra a disponibilidade
    // real antes de reservar.
    confirmedRescheduleAt: Type.Optional(Type.String()),
    // 2026-09-16 (bug de produção — followup indevido pós-encerramento). Sinal
    // explícito e estrutural de que ESTE turno encerra o assunto do lado do
    // cliente (agradecimento/despedida sem pergunta ou passo pendente) — não
    // existe intent dedicado pra isso (ver `Intent` acima) e inventar um
    // adicionaria um valor de enum com efeito colateral amplo (persistido em
    // `messages.intent`, consumido pelo dashboard/admin). Um booleano opcional
    // e aditivo é mais barato: o worker fecha a conversa (`status: 'closed'`)
    // só quando este campo vem `true` E nenhum outro intent estrutural
    // (confirma/cancela/reagenda/pede_humano/opt_out) já decidiu o status
    // nesta mesma resposta — ver `routeIntent`'s `reply_only` branch no
    // conversation-worker. Fail-open por padrão (`undefined`/`false` não muda
    // nada): a conversa só fecha quando a IA sinaliza isso explicitamente.
    // Requer instrução correspondente no prompt ATIVO da campanha (tabela
    // `prompts`, fora deste repositório) — sem isso o campo nunca vem `true`.
    conversationResolved: Type.Optional(Type.Boolean()),
});
//# sourceMappingURL=ai-output.js.map